import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/verticals";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, CurrencyEur, CalendarCheck, Star, UsersThree } from "@phosphor-icons/react";

const AdminProviderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) { navigate("/login"); return; }
    if (user && user.role === "admin") {
      api.get(`/admin/providers/${id}`).then((r) => setData(r.data)).catch(() => {});
    }
  }, [user, loading, id, navigate]);

  if (!user || user.role !== "admin" || !data) return null;
  const { provider, owner, services, staff, bookings, stats } = data;

  const cards = [
    { label: "Incassi", value: eur(stats.earnings), Icon: CurrencyEur, cls: "text-emerald-600" },
    { label: "Commissioni", value: eur(stats.commission), Icon: CurrencyEur, cls: "text-primary" },
    { label: "Prenotazioni", value: stats.total_bookings, Icon: CalendarCheck, cls: "text-blue-600" },
    { label: "Valutazione", value: stats.rating || "—", Icon: Star, cls: "text-amber-500" },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
      <Button variant="ghost" className="gap-1 mb-3" data-testid="admin-detail-back" onClick={() => navigate("/admin")}><ArrowLeft /> Torna al pannello</Button>
      <div className="flex items-center gap-4">
        <Avatar className="w-14 h-14"><AvatarImage src={provider.avatar} /><AvatarFallback>{provider.business_name[0]}</AvatarFallback></Avatar>
        <div>
          <h1 className="font-heading text-2xl font-bold">{provider.business_name}</h1>
          <div className="text-sm text-muted-foreground flex items-center gap-1.5 flex-wrap">{owner?.email} · {provider.city} · <Badge variant="secondary">{provider.vertical}</Badge></div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-card border border-border rounded-2xl p-4">
            <c.Icon size={20} className={c.cls} weight="duotone" />
            <div className="text-xl font-bold font-heading mt-2">{c.value}</div>
            <div className="text-xs text-muted-foreground">{c.label}</div>
          </div>
        ))}
      </div>

      <h2 className="font-heading text-lg font-bold mt-8 mb-2 flex items-center gap-2"><UsersThree size={18} weight="duotone" /> Staff ({staff.length})</h2>
      <div className="flex flex-wrap gap-2">
        {staff.map((s) => <Badge key={s.staff_id} variant="outline">{s.name} · {s.title || "Operatore"}</Badge>)}
        {staff.length === 0 && <span className="text-sm text-muted-foreground">Nessun membro</span>}
      </div>

      <h2 className="font-heading text-lg font-bold mt-8 mb-3">Prenotazioni ({bookings.length})</h2>
      <div className="border border-border rounded-2xl overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Servizio</TableHead><TableHead>Cliente</TableHead><TableHead>Operatore</TableHead><TableHead>Data</TableHead><TableHead>Prezzo</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
          <TableBody>
            {bookings.map((b) => (
              <TableRow key={b.booking_id}>
                <TableCell className="font-medium">{b.service_name}</TableCell>
                <TableCell>{b.customer_name}</TableCell>
                <TableCell>{b.staff_name || "—"}</TableCell>
                <TableCell>{b.date} {b.start_time}</TableCell>
                <TableCell>{eur(b.price)}</TableCell>
                <TableCell><Badge variant={b.payment_status === "paid" ? "default" : "secondary"}>{b.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground mt-3">Vista super admin in sola lettura.</p>
    </div>
  );
};

export default AdminProviderDetail;
